import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarDays,
  Wrench,
  DollarSign,
  FileText,
  ScrollText,
  FileWarning,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatDate, formatDateOnly } from "@/lib/cn";
import { TourLauncherButton } from "@/components/wizard/TourLauncherButton";

interface DashboardSummary {
  upcomingMeetings: Array<{ id: string; title: string; scheduledAt: string }>;
  openMaintenanceCount: number;
  overdueFeesCount: number;
  pendingPermitsCount: number;
  pendingResolutionsCount: number;
  expiringContracts: Array<{ id: string; vendorName: string; endDate: string }>;
}

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["dashboard-summary"],
    queryFn: async () => (await api.get<DashboardSummary>("/dashboard/summary")).data,
  });

  if (isLoading) return <div className="text-slate-500">{t("common.loading")}</div>;
  if (isError || !data) return <div className="text-red-600">{t("common.error")}</div>;

  const cards = [
    {
      labelKey: "dashboard.openMaintenance",
      value: data.openMaintenanceCount,
      icon: <Wrench className="text-orange-500" size={28} />,
      to: "/resident/maintenance",
    },
    {
      labelKey: "dashboard.overdueFees",
      value: data.overdueFeesCount,
      icon: <DollarSign className="text-red-500" size={28} />,
      to: "/finance/fees",
    },
    {
      labelKey: "dashboard.pendingPermits",
      value: data.pendingPermitsCount,
      icon: <FileText className="text-blue-500" size={28} />,
      to: "/resident/works-permit",
    },
    {
      labelKey: "dashboard.pendingResolutions",
      value: data.pendingResolutionsCount,
      icon: <ScrollText className="text-purple-500" size={28} />,
      to: "/resolutions",
    },
  ];

  return (
    <div className="space-y-6">
      <div data-tour="dashboard-welcome" className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("dashboard.title")}</h1>
          <p className="text-slate-600">{t("dashboard.subtitle")}</p>
        </div>
        <TourLauncherButton />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Link key={c.labelKey} to={c.to} className="card hover:shadow-md transition flex items-center justify-between">
            <div>
              <div className="text-sm text-slate-500">{t(c.labelKey)}</div>
              <div className="text-3xl font-bold text-slate-900 mt-1">{c.value}</div>
            </div>
            {c.icon}
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <CalendarDays size={18} /> {t("dashboard.upcomingMeetings")}
            </h2>
            <Link to="/meetings" className="text-sm text-primary hover:underline">
              {t("dashboard.viewAll")}
            </Link>
          </div>
          {data.upcomingMeetings.length === 0 ? (
            <p className="text-sm text-slate-500">{t("dashboard.noUpcomingMeetings")}</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.upcomingMeetings.map((m) => (
                <li key={m.id} className="py-2">
                  <div className="font-medium text-slate-800">{m.title}</div>
                  <div className="text-sm text-slate-500">{formatDate(m.scheduledAt, i18n.language)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FileWarning size={18} /> {t("dashboard.expiringContracts")}
            </h2>
            <Link to="/contracts" className="text-sm text-primary hover:underline">
              {t("dashboard.viewAll")}
            </Link>
          </div>
          {data.expiringContracts.length === 0 ? (
            <p className="text-sm text-slate-500">{t("dashboard.noExpiringContracts")}</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.expiringContracts.map((c) => {
                const days = Math.ceil(
                  (new Date(c.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                );
                const color = days < 30 ? "text-red-600" : days < 60 ? "text-amber-600" : "text-slate-500";
                return (
                  <li key={c.id} className="py-2 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-800">{c.vendorName}</div>
                      <div className="text-sm text-slate-500">{formatDateOnly(c.endDate, i18n.language)}</div>
                    </div>
                    <span className={`text-sm font-medium ${color}`}>{days}d</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
